<script setup lang="ts">
const { $pwa } = useNuxtApp()
const route = useRoute()
const signingOut = useState('auth-signing-out', () => false)
const { currentRole } = storeToRefs(useCurrentRoleStore())
const isProposalView = computed(
  () =>
    route.path.startsWith('/proposals/') ||
    (route.path.startsWith('/dashboard/proposals/') &&
      route.path.endsWith('/preview')),
)
const gameProgressStore = useGameProgressStore()
const { syncEvents } = useCurriculumReleaseCache()
const showStartupSplash = useState('startup-splash-visible', () => true)
let updateCheckTimer: ReturnType<typeof setInterval> | undefined

function dismissStartupSplash() {
  showStartupSplash.value = false
}

function handleOnline() {
  // Immediately try syncing unsynced progress when the connection is back.
  gameProgressStore.syncUnsyncedProgress()
  void syncEvents()
  checkForUpdate()
}

// Background checks must remain invisible: offline play is expected.
async function checkForUpdate() {
  if (!('serviceWorker' in navigator) || !navigator.onLine) return

  try {
    const registration = await navigator.serviceWorker.getRegistration()
    await registration?.update()
  } catch {
    // A later check will retry. Do not interrupt an offline learner.
  }
}

function handleVisibilityChange() {
  if (document.visibilityState === 'visible') checkForUpdate()
}

onMounted(async () => {
  gameProgressStore.startSyncTimer()
  void syncEvents()
  window.addEventListener('online', handleOnline)
  document.addEventListener('visibilitychange', handleVisibilityChange)
  updateCheckTimer = window.setInterval(() => checkForUpdate(), 5 * 60 * 1000)
  await checkForUpdate()
})

onUnmounted(() => {
  window.removeEventListener('online', handleOnline)
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  if (updateCheckTimer) window.clearInterval(updateCheckTimer)
  gameProgressStore.stopSyncTimer()
})

useHead({
  meta: [
    { charset: 'utf-8' },
    { name: 'viewport', content: 'width=device-width, initial-scale=1' },
    { key: 'theme-color', name: 'theme-color', content: '#1678e5' },
  ],
  link: [
    { rel: 'icon', href: '/icons/icon-192.png', type: 'image/png' },
    { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
  ],
  htmlAttrs: {
    lang: 'en',
  },
})

useSeoMeta({
  titleTemplate: '%s - WLearn Academy',
  ogImage: 'https://www.wlearn.me/images/wlearn-social-share.webp',
  twitterImage: 'https://www.wlearn.me/images/wlearn-social-share.webp',
  twitterCard: 'summary_large_image',
})
</script>

<template>
  <UApp>
    <div
      v-show="$pwa?.needRefresh"
      class="fixed inset-x-4 bottom-4 z-[110] mx-auto flex max-w-md items-center gap-3 rounded-[2rem] border-2 border-blue-100 bg-white p-4 shadow-[0_12px_28px_rgba(13,72,145,0.22)]"
    >
      <img
        src="/img/mascot/mascot-splash.webp"
        alt=""
        aria-hidden="true"
        class="w-12 shrink-0"
      />
      <span class="min-w-0 flex-1 text-sm font-medium text-slate-700">
        A fresh WLearn update is ready.
      </span>
      <button
        type="button"
        class="shrink-0 rounded-full bg-[#1678e5] px-4 py-2 font-[Fredoka] font-semibold text-white shadow-[0_4px_0_#0d4891]"
        @click="$pwa?.updateServiceWorker()"
      >
        Update
      </button>
    </div>
    <NuxtLoadingIndicator />
    <NuxtPwaManifest />
    <InstallPWABanner v-if="!isProposalView" />
    <div
      v-if="signingOut"
      role="status"
      aria-live="polite"
      class="flex min-h-screen flex-col items-center justify-center gap-4 bg-white text-[#1678e5]"
    >
      <UIcon
        name="i-lucide-loader-circle"
        class="size-12 animate-spin motion-reduce:animate-none"
      />
      <p class="font-[Fredoka] text-xl">Signing you out…</p>
    </div>
    <NuxtLayout v-else>
      <NuxtPage
        :page-key="
          (pageRoute) => `${pageRoute.path}:${currentRole.schoolId || ''}`
        "
      />
    </NuxtLayout>
    <MobileAppHeader v-if="!signingOut" />
    <AdventureBackgroundMusic />
    <MascotSplash
      v-if="showStartupSplash && !isProposalView"
      @done="dismissStartupSplash"
    />
  </UApp>
</template>
